import { Box, Typography } from '@material-ui/core';
import { FC, useCallback, useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useParams } from 'react-router-dom';
import { useMounted } from 'src/hooks/use-mounted';
import { BreadCrumbView } from 'src/components/breadcrumbs';
import { ResponseCodes } from 'src/enums/responseCodes';
import { ResourceLoading } from 'src/components/resource-loading';
import { NotFound } from 'src/containers/not-found';
import { Technician, TechnicianBasicProfile } from 'src/interfaces/new-technician/technician';
import { technicianApi } from 'src/api/newTechnician/technician';
import toast from 'react-hot-toast';
import { CircularLoader } from 'src/components/common/circularLoader';
import { s3FileApi } from 'src/api/s3file';
import { BankInfoCard } from 'src/components/common/bankDetails/bankInfoCard';
import { BankDetails, BrandsProducts, ChannelPartner, DocumentDetails } from 'src/interfaces/new-channel-partner/channelPartner';
import { ShowBrandsProductsCard } from 'src/components/common/ProductBrandSection/showBrandsProducts';
import { channelPartnerApi } from 'src/api/newChannelPartner/channelPartner';
import { KycDocumentsCard } from 'src/components/new-channel-partner/addChannelPartner/cards/channelPartnerDetails/kycDocumentsCard';
import { ServiceAreaCard } from 'src/components/new-channel-partner/addChannelPartner/cards/channelPartnerDetails/serviceAreaCard';
import { StatusDetailsCard } from 'src/components/new-channel-partner/addChannelPartner/cards/StatusDetailsCard';
import { AccessDenied } from 'src/containers/access-denied';
import { useAuth } from 'src/hooks/use-auth';
import { Roles } from 'src/enums/role-based-auth-guard';
import { REPAIR_SERVICE_ID } from 'src/constants/repairRequest-constants';
import { ActivityHistoryLog } from 'src/components/common/activity-history-table';
import { ResourceError } from '../../resource-error';
import { BasicInfoCard } from './cards/technicianDetails/basicInfoCard';
import { ServiceInfoCard } from './cards/technicianDetails/serviceInfoCard';
import { SubmitProfileCard } from './cards/technicianDetails/submitProfileCard';
import { TechnicianApprovalCard } from './cards/approvalCard';

interface TechnicianDetailsState {
  data?: Technician;
  error?: string;
  isLoading: boolean;
  statusCode?: number;
}

export const ApproveTechnician: FC = () => {
  const { id } = useParams();
  const mounted = useMounted();
  const auth = useAuth();
  const [technicianDetailsState,
    setTechnicianDetailsState] = useState<TechnicianDetailsState>({ isLoading: true });
  const [loading, setLoading] = useState<boolean>(false);
  const [cpDetails, setCpDetails] = useState<ChannelPartner>();
  const [activityLogs, setActivityLogs] = useState([]);
  const breadCrumbsDetails = [
    {
      url: '/dashboard/technicians/main/pending',
      label: 'Technicians'
    },
    {
      url: '',
      label: 'Technician Details'
    }
  ];

  const getChannelPartnerDetails = async (cpId: string) => {
    const result = await channelPartnerApi
      .getChannelPartnerById(cpId);
    setCpDetails(result.responseData);
  };

  const updateTechnicianState = (data) => {
    setTechnicianDetailsState(() => ({
      isLoading: false,
      data
    }));
  };
  const getTechnicianDetails = useCallback(async () => {
    setTechnicianDetailsState(() => ({ isLoading: true }));
    let resultData;
    try {
      resultData = await technicianApi.getTechnicianById(id);
      if (mounted.current) {
        setTechnicianDetailsState(() => ({
          isLoading: false,
          data: resultData?.responseData,
          statusCode: resultData?.statusCode
        }));
        getChannelPartnerDetails(resultData?.responseData?.channelPartner?.id);
      }
    } catch (err) {
      console.error(err);

      if (mounted.current) {
        setTechnicianDetailsState(() => ({
          isLoading: false,
          error: err.message,
          statusCode: resultData?.statusCode
        }));
      }
    }
  }, []);

  const updateBasicDetails = async (basicInfo: TechnicianBasicProfile): Promise<boolean> => {
    setLoading(true);
    const updateResult = await technicianApi.updateBasicDetails(
      basicInfo, technicianDetailsState?.data?.id
    );
    if (updateResult.statusCode === ResponseCodes.OK) {
      toast.success('Basic Details Updated');
      updateTechnicianState(updateResult?.responseData);
      setLoading(false);
      return true;
    }
    toast.error(updateResult?.responseData?.message?.toString());
    setLoading(false);
    return false;
  };

  const addProfilePhoto = async (selectedFile: any) => {
    const isProfilePicAvailable = technicianDetailsState.data?.profileImage?.name;
    setLoading(true);
    const uploadResult = await s3FileApi.uploadMultiple('technician', technicianDetailsState?.data?.id, [selectedFile]);
    const fileDoc = {
      name: selectedFile?.name,
      type: selectedFile?.type,
      key: uploadResult.data[0].key
    };
    const updateResult = await technicianApi.addProfilePhoto(
      fileDoc, technicianDetailsState?.data?.id
    );
    if (updateResult.statusCode === ResponseCodes.OK) {
      if (isProfilePicAvailable) {
        toast.success('Profile Photo Updated');
      } else {
        toast.success('Profile Photo Uploaded');
      }
      updateTechnicianState(updateResult?.responseData);
      setLoading(false);
      return true;
    }
    toast.error(updateResult?.responseData?.message?.toString());
    setLoading(false);
    return false;
  };

  const updateBankDetails = async (bankInfo: BankDetails): Promise<boolean> => {
    setLoading(true);
    const isIfscExist = technicianDetailsState?.data?.bankDetails?.ifscCode;
    const updateResult = await technicianApi.updateBankDetails(
      bankInfo, technicianDetailsState?.data?.id
    );
    if (updateResult.statusCode === ResponseCodes.OK) {
      if (isIfscExist) {
        toast.success('Bank Details Updated');
      } else {
        toast.success('Bank Details Added');
      }
      updateTechnicianState(updateResult?.responseData);
      setLoading(false);
      return true;
    }
    toast.error(updateResult?.responseData?.message?.toString());
    setLoading(false);
    return false;
  };

  const addBrand = async (brandProducts: BrandsProducts): Promise<boolean> => {
    setLoading(true);
    const updateResult = await technicianApi.addBrand(
      brandProducts, technicianDetailsState?.data?.id
    );
    if (updateResult.statusCode === ResponseCodes.OK) {
      toast.success('Brand and Products Added');
      updateTechnicianState(updateResult?.responseData);
      setLoading(false);
      return true;
    }
    toast.error(updateResult?.responseData?.message?.toString());
    setLoading(false);
    return false;
  };

  const updateBrand = async (brandProducts: BrandsProducts): Promise<boolean> => {
    setLoading(true);
    const updateResult = await technicianApi.updateBrand(
      brandProducts, technicianDetailsState?.data?.id
    );
    if (updateResult.statusCode === ResponseCodes.OK) {
      toast.success('Products Updated');
      updateTechnicianState(updateResult?.responseData);
      setLoading(false);
      return true;
    }
    toast.error(updateResult?.responseData?.message?.toString());
    setLoading(false);
    return false;
  };

  const deleteBrand = async (brandId: string) => {
    setLoading(true);
    const updateResult = await technicianApi.deleteBrand(
      brandId, technicianDetailsState?.data?.id
    );
    if (updateResult.statusCode === ResponseCodes.OK) {
      toast.success('Brand Deleted');
      updateTechnicianState(updateResult?.responseData);
    } else {
      toast.error(updateResult?.responseData);
    }
    setLoading(false);
  };

  const addDocument = async (document: DocumentDetails, selectedFile: any): Promise<boolean> => {
    setLoading(true);
    const uploadResult = await s3FileApi.uploadMultiple('technician', technicianDetailsState?.data?.id, [selectedFile]);
    const fileDoc = { ...document };
    fileDoc.file.key = uploadResult.data[0].key;
    const updateResult = await technicianApi.addDocument(
      fileDoc, technicianDetailsState?.data?.id
    );
    if (updateResult.statusCode === ResponseCodes.OK) {
      toast.success('Document Uploaded');
      updateTechnicianState(updateResult?.responseData);
      setLoading(false);
      return true;
    }
    toast.error(updateResult?.responseData?.message?.toString());
    setLoading(false);
    return false;
  };

  const deleteDocument = async (objectId: string) => {
    setLoading(true);
    const updateResult = await technicianApi.deleteDocument(
      objectId, technicianDetailsState?.data?.id
    );
    if (updateResult.statusCode === ResponseCodes.OK) {
      toast.success('Document Deleted');
      updateTechnicianState(updateResult?.responseData);
      setLoading(false);
      return true;
    }
    toast.error(updateResult?.responseData?.message?.toString());
    setLoading(false);
    return false;
  };

  const addServiceAreas = async (serviceAreaIds: string[]): Promise<boolean> => {
    setLoading(true);
    const updateResult = await technicianApi.addServiceAreas(
      serviceAreaIds, technicianDetailsState?.data?.id
    );
    if (updateResult.statusCode === ResponseCodes.OK) {
      toast.success('Service Areas Added');
      updateTechnicianState(updateResult?.responseData);
      setLoading(false);
      return true;
    }
    toast.error(updateResult?.responseData?.message?.toString());
    setLoading(false);
    return false;
  };

  const deleteServiceArea = async (serviceAreaIds: string) => {
    setLoading(true);
    const updateResult = await technicianApi.deleteServiceArea(
      serviceAreaIds, technicianDetailsState?.data?.id
    );
    if (updateResult.statusCode === ResponseCodes.OK) {
      toast.success('ServiceArea Deleted');
      updateTechnicianState(updateResult?.responseData);
    } else {
      toast.error(updateResult?.responseData);
    }
    setLoading(false);
  };

  const getActivityLogs = async () => {
    const { responseData } = await channelPartnerApi.getActivityLogsById(id);
    const sortedActivity = responseData.sort((a, b) => new Date(
      b.timestamp
    ).getTime() - new Date(a.timestamp).getTime());
    setActivityLogs(sortedActivity);
  };

  const isRepairServicePresent = () => (technicianDetailsState.data?.services?.filter(
    (item) => item.id === REPAIR_SERVICE_ID
  ).length > 0);

  useEffect(() => {
    getTechnicianDetails().catch(console.error);
  }, []);

  useEffect(() => {
    getActivityLogs().catch(console.error);
  }, [loading]);

  const renderContent = () => {
    if (technicianDetailsState.isLoading) {
      return <ResourceLoading />;
    }
    if (technicianDetailsState.statusCode === ResponseCodes.FORBIDDEN) {
      return <AccessDenied />;
    }

    if (technicianDetailsState.error
      && technicianDetailsState.statusCode !== ResponseCodes.NOT_FOUND) {
      return <ResourceError />;
    }

    if (!technicianDetailsState.data) {
      return <NotFound />;
    }

    if (technicianDetailsState.statusCode === ResponseCodes.NOT_FOUND) {
      return <NotFound />;
    }
    return (
      <>
        <Box
          sx={{
            flexGrow: 1
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              px: 2
            }}
          >
            <Box sx={{ py: 4 }}>
              <Box
                sx={{
                  alignItems: 'center',
                  display: 'flex',
                  pb: 3
                }}
              >
                <Typography
                  color="textPrimary"
                  variant="h4"
                >
                  {technicianDetailsState.data.firstName}
                  &nbsp;
                  {technicianDetailsState.data.lastName}
                </Typography>
              </Box>
              <BreadCrumbView breadCrumbs={breadCrumbsDetails} />
            </Box>

            <Box
              sx={{
                flexGrow: 1
              }}
            >
              <StatusDetailsCard
                type="Technician"
                status={technicianDetailsState.data.status}
                activityLogs={activityLogs}
              />
              <BasicInfoCard
                technicianDetailData={technicianDetailsState.data}
                addProfilePhoto={addProfilePhoto}
                updateBasicDetails={updateBasicDetails}
              />
              <KycDocumentsCard
                scope="Technician"
                documentDetails={technicianDetailsState.data.documentDetails}
                addDocument={addDocument}
                deleteDocument={deleteDocument}
              />
              <BankInfoCard
                bankDetails={technicianDetailsState.data?.bankDetails}
                updateBankDetails={updateBankDetails}
              />
              <ServiceAreaCard
                scope="Technician"
                cpServiceAreaIds={cpDetails?.serviceAreaIds}
                serviceAreaIds={technicianDetailsState.data?.serviceAreaIds}
                addServiceAreas={addServiceAreas}
                deleteServiceArea={deleteServiceArea}
              />
              <ServiceInfoCard
                cpServiceTypes={cpDetails?.services}
              />
              {isRepairServicePresent() && (
              <ShowBrandsProductsCard
                brandsProducts={technicianDetailsState.data?.brandsProducts}
                cpBrandsProducts={cpDetails?.brandsProducts}
                scope="Technician"
                addBrand={addBrand}
                updateBrand={updateBrand}
                deleteBrand={deleteBrand}
              />
              )}
              {auth.user.scope === Roles.WEB_ADMIN && (
              <TechnicianApprovalCard
                technician={technicianDetailsState.data}
                setLoading={setLoading}
              />
              )}
              <SubmitProfileCard
                technicianData={technicianDetailsState.data}
                submitProfile={null}
              />
              <ActivityHistoryLog
                id={technicianDetailsState.data?.id}
              />
            </Box>
          </Box>
        </Box>
        { loading && (
        <CircularLoader />
        )}
      </>
    );
  };

  return (
    <>
      <Helmet>
        <title>Technician | ServCare</title>
      </Helmet>
      <Box
        sx={{
          backgroundColor: 'background.default',
          flexGrow: 1
        }}
      >
        { technicianDetailsState && renderContent()}
      </Box>
    </>
  );
};
